// Trims a fixed amount off both ends of a RINEX 2.x observation file's epoch series.
// Ported from SafeHarborBase.exe's TrimOperation (a Trimble-R10-only WPF desktop tool
// that can't run in a browser, or headless on Linux — its RinexObsParser/Writer logic
// is plain fixed-width text manipulation, reimplemented here client-side instead).
//
// Also duplicated at services/ais-api/src/rinex-trim.js for the CloudShare automation
// engine (that Docker build can't reach this top-level src/ tree) — keep both in sync.
//
// RINEX 2.11 epoch records don't carry their own line count; each satellite's
// observations span ceil(numObsTypes / 5) lines (5 values per line max), so the number
// of body lines belonging to an epoch has to be computed from the header's declared
// obs-type count and that epoch's satellite count, not assumed fixed.

const EPOCH_RE = /^ ?(\d{1,2})\s+(\d{1,2})\s+(\d{1,2})\s+(\d{1,2})\s+(\d{1,2})\s+(\d{1,2}\.\d{4,7})\s+([0-6])\s*(\d{1,3})/;

function formatTimeOfFirstObsLine(originalLine, date) {
  const i6 = (n) => String(n).padStart(6, " ");
  const y = date.getUTCFullYear();
  const mo = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  const h = date.getUTCHours();
  const mi = date.getUTCMinutes();
  const s = date.getUTCSeconds() + date.getUTCMilliseconds() / 1000;
  // Fields are 5x I6 + F13.7 = 43 fixed columns (RINEX 2.11 §5); everything after
  // that (time system tag + label) is untouched since only the timestamp changes.
  const secField = s.toFixed(7).padStart(13, " ");
  return i6(y) + i6(mo) + i6(d) + i6(h) + i6(mi) + secField + originalLine.slice(43);
}

export function isRinexObsText(text) {
  return /RINEX VERSION\s*\/\s*TYPE/.test(String(text || "").slice(0, 200))
    && /OBSERVATION DATA/.test(String(text || "").slice(0, 200));
}

// First/last epoch timestamps and span, without walking the full body (no need for the
// obs-type/satellite-count line-accounting trimRinexObs does — just the two endpoints).
// Used to pick OPUS-S vs OPUS-RS: OPUS now hard-rejects anything under 2 hours on OPUS-S
// ("Datasets less than two hours in duration no longer accepted to OPUS-S"), and Safe
// Harbor control-point sessions are routinely ~20-40 minutes, squarely OPUS-RS territory.
export function getRinexObsTimeSpan(text) {
  const lines = String(text || "").split(/\r?\n/);
  let first = null;
  let last = null;
  for (const line of lines) {
    const m = EPOCH_RE.exec(line);
    if (!m) continue;
    const [, yy, mo, dd, hh, mi, ss] = m;
    const epochMs = Date.UTC(2000 + parseInt(yy, 10), parseInt(mo, 10) - 1, parseInt(dd, 10), parseInt(hh, 10), parseInt(mi, 10), 0)
      + parseFloat(ss) * 1000;
    if (first === null) first = epochMs;
    last = epochMs;
  }
  if (first === null) return null;
  return { startMs: first, endMs: last, durationSec: (last - first) / 1000 };
}

// trimStartSec/trimEndSec: seconds of epochs to drop from the front/back of the session.
// minKeepSec: refuse to trim (return trimmed:false) if fewer than this many seconds of
// observation would remain — OPUS itself requires at least 15 minutes of static data,
// so silently producing a too-short file would just trade one failure for another.
export function trimRinexObs(text, { trimStartSec = 120, trimEndSec = 120, minKeepSec = 900 } = {}) {
  const nl = text.includes("\r\n") ? "\r\n" : "\n";
  const lines = text.split(/\r?\n/);
  const headerEndIdx = lines.findIndex((l) => l.includes("END OF HEADER"));
  if (headerEndIdx === -1) throw new Error("No END OF HEADER line found — not a RINEX obs file.");

  const obsTypesLine = lines.slice(0, headerEndIdx).find((l) => l.includes("# / TYPES OF OBSERV"));
  const nTypes = obsTypesLine ? parseInt(obsTypesLine.trim().split(/\s+/)[0], 10) : NaN;
  const linesPerSat = Math.max(1, Math.ceil((Number.isFinite(nTypes) && nTypes > 0 ? nTypes : 5) / 5));

  const blocks = [];
  let i = headerEndIdx + 1;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") { i++; continue; }
    const m = EPOCH_RE.exec(line);
    if (!m) { i++; continue; } // tolerate stray blank/garbage lines rather than aborting
    const [, yy, mo, dd, hh, mi, ss, , numsatStr] = m;
    const numsat = parseInt(numsatStr, 10);
    const epochMs = Date.UTC(2000 + parseInt(yy, 10), parseInt(mo, 10) - 1, parseInt(dd, 10), parseInt(hh, 10), parseInt(mi, 10), 0)
      + parseFloat(ss) * 1000;
    const blockLen = 1 + numsat * linesPerSat;
    blocks.push({ start: i, end: Math.min(i + blockLen, lines.length), epochMs });
    i += blockLen;
  }
  if (!blocks.length) throw new Error("No observation epochs found.");

  const keepFromMs = blocks[0].epochMs + trimStartSec * 1000;
  const keepToMs = blocks[blocks.length - 1].epochMs - trimEndSec * 1000;
  const kept = blocks.filter((b) => b.epochMs >= keepFromMs && b.epochMs <= keepToMs);

  const keptSpanSec = kept.length ? (kept[kept.length - 1].epochMs - kept[0].epochMs) / 1000 : 0;
  if (!kept.length || keptSpanSec < minKeepSec) {
    return { trimmed: false, text, reason: "not enough observation time remains after trimming", originalEpochs: blocks.length };
  }

  const bodyLines = [];
  for (const b of kept) for (let l = b.start; l < b.end; l++) bodyLines.push(lines[l]);

  const firstKeptDate = new Date(kept[0].epochMs);
  const header = lines.slice(0, headerEndIdx + 1)
    .map((l) => (l.includes("TIME OF FIRST OBS") ? formatTimeOfFirstObsLine(l, firstKeptDate) : l));

  return {
    trimmed: true,
    text: [...header, ...bodyLines].join(nl) + nl,
    originalEpochs: blocks.length,
    keptEpochs: kept.length,
    removedFromStart: blocks.indexOf(kept[0]),
    removedFromEnd: blocks.length - 1 - blocks.indexOf(kept[kept.length - 1]),
  };
}
